import { useCallback, useEffect, useMemo } from "react";

import { FormProvider, UseFormReturn } from "react-hook-form";

import { Box, Grid } from "@mui/material";

import {
  FormAddSomeoneElseToggle,
  PersonalDetailsWithExtraQuestionsForm,
} from "@covertree/new-business-forms";
import {
  AdditionalInsuredInput,
  PolicyholderType,
} from "@covertree/new-business-graphql";
import {
  selectAdditionalInsured,
  selectIsBookConversion,
  setAdditionalInsured,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { Button, Form, FormPrimaryInsuredTypeSelect } from "@covertree/ui";
import {
  InformationBox,
  PersonalDetailsDisclaimer,
} from "@covertree/ui/content";
import { mapAdditionalInsuredRelationship } from "@covertree/utils";
import {
  DEFAULT_SEQUOIA_PHONE_NUMBER,
  DEFAULT_SEQUOIA_DATE_OF_BIRTH,
  DEFAULT_SEQUOIA_EMAIL_PREFIX,
  DEFAULT_SEQUOIA_EMAIL_DOMAIN,
} from "@covertree/consts";

import { useAppDispatch } from "@redux/hooks";
import { useScrollToNextButton } from "@hooks/scroll/useScrollToNextButton";
import { NEW_POLICY_SUBMIT_BUTTON_ID } from "@consts/index";

import { ExtraQuestions } from "./ExtraQuestions";
import { PolicyholderPersonFormFields } from "./PolicyholderPersonFormFields";
import { PolicyholderOrganizationFormFields } from "./PolicyholderOrganizationFormFields";

interface PersonalDetailsViewProps {
  form: UseFormReturn<PersonalDetailsWithExtraQuestionsForm>;
  onSubmit: (formValues: PersonalDetailsWithExtraQuestionsForm) => void;
}

export function PersonalDetailsView({
  form,
  onSubmit,
}: PersonalDetailsViewProps) {
  const dispatch = useAppDispatch();

  const additionalInsured = useNewBusinessSelector(selectAdditionalInsured);
  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isSubmitSuccessful },
    trigger,
    setValue,
    getValues,
  } = form;

  const policyholderType = watch("type");
  const policyholderName = watch("firstName");

  const handleAdditionalInsuredFormSubmit = useCallback(
    (additionalInsured: AdditionalInsuredInput) => {
      dispatch(setAdditionalInsured(additionalInsured));
    },
    [dispatch, additionalInsured]
  );

  const handleToggleAdditionalPersonSwitch = useCallback(() => {
    if (additionalInsured) {
      dispatch(setAdditionalInsured(undefined));
    }
  }, [dispatch, additionalInsured]);

  const additionalPersonDetails = useMemo(
    () =>
      additionalInsured
        ? `${additionalInsured.firstName} ${additionalInsured.lastName}, ${mapAdditionalInsuredRelationship(additionalInsured.relationship)}`
        : undefined,
    [additionalInsured]
  );

  useEffect(() => {
    if (!isBookConversion) return;

    if (getValues("primaryContactNumber") === DEFAULT_SEQUOIA_PHONE_NUMBER) {
      setValue("primaryContactNumber", "");
    }
    if (getValues("insuredDateOfBirth") === DEFAULT_SEQUOIA_DATE_OF_BIRTH) {
      setValue("insuredDateOfBirth", null);
    }

    const email = getValues("emailAddress");
    if (
      email &&
      email.startsWith(DEFAULT_SEQUOIA_EMAIL_PREFIX) &&
      email.endsWith(DEFAULT_SEQUOIA_EMAIL_DOMAIN)
    ) {
      setValue("emailAddress", "");
    }
    trigger();
  }, [isBookConversion, form, trigger]);

  useScrollToNextButton(isValid && !isSubmitSuccessful);

  return (
    <>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(onSubmit)} id="personal-details-form">
          <Grid container spacing={3}>
            <Grid item md={12}>
              <FormPrimaryInsuredTypeSelect control={control} />
            </Grid>
            {policyholderType === PolicyholderType.Person ? (
              <PolicyholderPersonFormFields form={form} />
            ) : (
              <PolicyholderOrganizationFormFields form={form} />
            )}
            <ExtraQuestions />
          </Grid>
        </Form>
      </FormProvider>
      <Grid container spacing={3}>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <FormAddSomeoneElseToggle
            firstPersonName={policyholderName}
            checked={!!additionalInsured}
            subheader={additionalPersonDetails}
            onSubmit={handleAdditionalInsuredFormSubmit}
            onToggle={handleToggleAdditionalPersonSwitch}
          />
        </Grid>
        <Grid item xs={12}>
          <InformationBox data-testid="disclaimer-box">
            By entering a phone number, you are indicating that you have
            received permission for CoverTree to text or call the applicant
            about your quote or other CoverTree services, including with an
            automated dialing system. The applicant can opt out at any time.
          </InformationBox>
        </Grid>
        <Grid item xs={12}>
          <PersonalDetailsDisclaimer />
        </Grid>
      </Grid>
      <Box paddingY={4}>
        <Button
          type="submit"
          form="personal-details-form"
          id={NEW_POLICY_SUBMIT_BUTTON_ID}>
          {isBookConversion ? "View Quote" : "Get Quote"}
        </Button>
      </Box>
    </>
  );
}
